import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import Fuse from 'fuse.js';
import noImage from '../assets/img/brand/noImage.png';

const MONTSERRAT = 'Montserrat';

export default function MultiSelectSearch({
  label,
  tooltip,
  options = [],
  selected = [],
  setSelected,
  keysToSearch = ['name'],
  idKey = 'id',
  displayKey = 'name',
  subTitleKey = null,
  subTitleFn = null,
  logoKey = 'logo',
  multiple = true,
  currentBrandId = null,
  fallbackImage = null,
  error = false,
  triggerValidation = null,
  placeholder = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const safeSelected = Array.isArray(selected) ? selected : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && !multiple && safeSelected.length === 0 && search.trim() !== '') {
      setSearch('');
    }
  }, [isOpen, multiple, safeSelected.length, search]);

  // Auto-focus input when open
  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Base filtered options (excluding archived + self brand)
  const baseOptions = useMemo(() =>
    options.filter(o => !o.is_archived && Number(o[idKey]) !== Number(currentBrandId)),
    [options, idKey, currentBrandId]
  );

  // Augment options with a flat hierarchy text field for searching parent names too
  const augmentedOptions = useMemo(() =>
    baseOptions.map(o => ({
      ...o,
      _hierarchyText: [
        ...(o.brand_hierarchy || []).map(b => b.name),
        ...(o.category_hierarchy || []).map(c => c.name),
      ].filter(Boolean).join(' '),
    })),
    [baseOptions]
  );

  const fuse = useMemo(() => new Fuse(augmentedOptions, {
    keys: [...keysToSearch, '_hierarchyText'],
    threshold: 0.3,
    includeMatches: true,
  }), [augmentedOptions, keysToSearch]);

  const filteredOptions = search
    ? fuse.search(search)
    : augmentedOptions.map(item => ({ item }));

  const highlightMatch = (text, indices) => {
    if (!indices) return text;
    let result = [];
    let lastIndex = 0;
    indices.forEach(([start, end]) => {
      if (start > lastIndex) result.push(text.slice(lastIndex, start));
      result.push(<span key={start}>{text.slice(start, end + 1)}</span>);
      lastIndex = end + 1;
    });
    if (lastIndex < text.length) result.push(text.slice(lastIndex));
    return result;
  };

  const handleSelect = (item) => {
    if (triggerValidation) triggerValidation(item);
    if (multiple) {
      if (!safeSelected.some(s => s[idKey] === item[idKey])) {
        setSelected([...safeSelected, item]);
      }
    } else {
      setSelected([item]);
    }
    setSearch('');
    setIsOpen(false);
  };

  const handleRemove = (id) => {
    if (multiple) {
      setSelected(safeSelected.filter(s => s[idKey] !== id));
    } else {
      setSelected([]);
    }
  };

  return (
    <div className="multi-select" ref={containerRef} style={{ position: 'relative' }}>
      <label htmlFor="label" className="form-label d-flex align-items-center gap-1" style={{ fontSize: '12px', fontFamily: MONTSERRAT }}>
        {label}
        {tooltip}
      </label>

      <div
        className="select-box form-control"
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          cursor: 'text',
          paddingRight: '4px',
          border: `1px solid ${error ? '#dc3545' : '#6C757D'}`,
          height: '40px',
          fontFamily: MONTSERRAT,
        }}
      >
        {multiple
          ? safeSelected.map(item => (
            <div
              key={item[idKey]}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                background: '#f1f3f5',
                borderRadius: '6px',
                padding: '2px 6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {item[logoKey] && label !== "Category" && !label?.includes("Brand") && (
                  <img
                    src={item[logoKey]}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain' }}
                  />
                )}
                <span style={{ fontFamily: MONTSERRAT, color: "#181818", fontSize: "14px", fontWeight: "400", lineHeight: "18px" }}>
                  {item[displayKey]}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemove(item[idKey]); }}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))
          : safeSelected[0] && (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {safeSelected[0][logoKey] && label !== "Category" && !label?.includes("Brand") && (
                  <img
                    src={safeSelected[0][logoKey]}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain', marginRight: 5 }}
                  />
                )}
                <span style={{ fontFamily: MONTSERRAT, color: "#181818", fontSize: "14px", fontWeight: "400", lineHeight: "18px", marginTop: '-4px' }}>
                  {safeSelected[0][displayKey]}
                </span>
              </div>
            </div>
          )}

        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            if (multiple || !safeSelected.length) setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          placeholder={safeSelected.length === 0 ? placeholder : ''}
          style={{
            border: 'none',
            flex: 1,
            outline: 'none',
            minHeight: '24px',
            background: 'transparent',
            visibility: !multiple && safeSelected.length > 0 ? 'hidden' : 'visible',
            alignSelf: 'center',
            lineHeight: 'normal',
            fontFamily: MONTSERRAT,
            fontSize: '14px',
          }}
          disabled={!multiple && safeSelected.length > 0}
        />

        {/* Icons area */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {/* X icon — only when a value is selected in single mode */}
          {!multiple && safeSelected.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRemove(safeSelected[0][idKey]); setSearch(''); }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', marginTop: '-4px' }}
              title="Clear"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M9 3L3 9M3 3L9 9" stroke="#212529" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          {/* Dropdown arrow */}
          <div
            style={{ borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: '0.2s', cursor: 'pointer', marginRight: '7px', marginTop: '-4px' }}
            onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"
              style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: '0.2s' }}>
              <path d="M4.5 11.25L9 6.75L13.5 11.25" stroke="#212529" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            minWidth: 0,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '6px',
            maxHeight: '250px',
            overflowY: 'auto',
            zIndex: 1000,
            boxSizing: 'border-box',
          }}
        >
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredOptions.length > 0
              ? filteredOptions.map(({ item, matches }) => {
                const match = matches?.find(m => keysToSearch.includes(m.key));
                return (
                  <li
                    key={item[idKey]}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setHoveredItem(item[idKey])}
                    onMouseLeave={() => setHoveredItem(null)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      backgroundColor: hoveredItem === item[idKey] ? '#FFFDE7' : 'transparent',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                      {label !== "Category" && (
                        <img
                          src={item[logoKey] || noImage}
                          alt={item[displayKey]}
                          style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', background: '#f5f5f5' }}
                        />
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {label === "Category" ? (
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontFamily: MONTSERRAT, fontSize: '14px', fontWeight: 400, lineHeight: '18px', color: '#212529' }}>
                              {item.category_name}
                            </span>
                            {item.category_hierarchy && item.category_hierarchy.length >= 1 && (
                              <span style={{ fontFamily: MONTSERRAT, fontSize: "12px", color: "#6C757D", fontWeight: 400 }}>
                                {item.category_hierarchy.map(c => c.name).join(' >> ')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            <span style={{ fontFamily: MONTSERRAT, fontSize: '14px', fontWeight: 400, lineHeight: '18px', color: '#212529' }}>
                              {highlightMatch(item[displayKey], match?.indices)}
                            </span>
                            {subTitleFn && subTitleFn(item) && (
                              <span style={{ fontFamily: MONTSERRAT, fontSize: "12px", color: "#6C757D", fontWeight: 400 }}>
                                {subTitleFn(item)}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })
              : (
                <li style={{ padding: '10px 12px', fontFamily: MONTSERRAT, fontSize: '14px', color: '#6C757D', textAlign: 'center' }}>
                  No results found
                </li>
              )
            }
          </ul>
        </div>
      )}
    </div>
  );
}
